import React, { createContext, useContext, useEffect, useState } from 'react';

import { getCurrentBrandId } from '../config/branding/brand-registry';
import type {
  BrandConfig,
  BrandingContextValue,
} from '../config/branding/branding.types';
import { getBrandById } from '../config/branding/brands';
import { logger } from '../utils/logger';

/**
 * Branding context for providing brand configuration throughout the app
 */
const BrandingContext = createContext<BrandingContextValue | undefined>(
  undefined,
);

/**
 * Update document metadata based on brand configuration
 */
const updateDocumentMetadata = (brand: BrandConfig) => {
  // Update title
  document.title = brand.metadata.title;

  // Update or create favicon
  let favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
  if (!favicon) {
    favicon = document.createElement('link');
    favicon.rel = 'icon';
    document.head.appendChild(favicon);
  }
  favicon.href = brand.assets.logo.favicon;

  // Update or create meta description
  let metaDescription = document.querySelector(
    'meta[name="description"]',
  ) as HTMLMetaElement;
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    document.head.appendChild(metaDescription);
  }
  metaDescription.content = brand.metadata.description;

  // Update or create meta keywords
  if (brand.metadata.keywords && brand.metadata.keywords.length > 0) {
    let metaKeywords = document.querySelector(
      'meta[name="keywords"]',
    ) as HTMLMetaElement;
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = brand.metadata.keywords.join(', ');
  }
};

interface BrandingProviderProps {
  children: React.ReactNode;
  /** Optional brand override for testing */
  overrideBrandId?: string;
}

/**
 * Provider component for branding context
 */
export const BrandingProvider: React.FC<BrandingProviderProps> = ({
  children,
  overrideBrandId,
}) => {
  const [brand, setBrand] = useState<BrandConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get brand ID based on domain or override
    const brandId = overrideBrandId || getCurrentBrandId();

    // Load brand configuration
    const selectedBrand = getBrandById(brandId);
    setBrand(selectedBrand);

    // Apply brand to document
    updateDocumentMetadata(selectedBrand);

    // Mark as loaded
    setIsLoading(false);

    // Log for debugging
    logger.debug('Brand loaded', {
      id: selectedBrand.id,
      name: selectedBrand.name,
    });
  }, [overrideBrandId]);

  // Don't render children until brand is loaded
  if (!brand) {
    return null;
  }

  const value: BrandingContextValue = {
    brand,
    isLoading,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

/**
 * Hook to access the current brand configuration
 */
export const useBrandingContext = (): BrandingContextValue => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBrandingContext must be used within BrandingProvider');
  }
  return context;
};

/**
 * Hook to access just the brand configuration
 */
export const useBrand = (): BrandConfig => {
  const { brand } = useBrandingContext();
  return brand;
};

/**
 * Hook to access brand theme tokens
 */
export const useBrandTheme = () => {
  const { brand } = useBrandingContext();
  return brand.theme;
};

/**
 * Hook to update page title with brand template
 */
export const usePageTitle = (pageTitle?: string) => {
  const { brand } = useBrandingContext();

  useEffect(() => {
    if (pageTitle && brand.metadata.titleTemplate) {
      // Apply template
      document.title = brand.metadata.titleTemplate.replace('%s', pageTitle);
    } else if (pageTitle) {
      // No template, just use page title
      document.title = `${pageTitle} | ${brand.metadata.title}`;
    } else {
      // No page title, use brand title
      document.title = brand.metadata.title;
    }

    // Cleanup: reset to brand title
    return () => {
      document.title = brand.metadata.title;
    };
  }, [pageTitle, brand]);
};
